import { Play, Square, RotateCcw } from "lucide-react";
import { FORGE } from "@/constants/testIds";
import ReferenceUploader from "@/components/ReferenceUploader";

export default function BriefComposer({
    brief,
    setBrief,
    llmKey,
    setLlmKey,
    brandIdx,
    setBrandIdx,
    contentIdx,
    setContentIdx,
    aspectIdx,
    setAspectIdx,
    files,
    setFiles,
    config,
    running,
    onRun,
    onStop,
    onLoadDemo,
    error,
}) {
    if (!config) return null;

    const disabled = running || !brief.trim();

    return (
        <div className="bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient p-6 md:p-8">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--profecia-gold)] font-mono">
                        Command Center
                    </div>
                    <h2 className="font-serif text-2xl md:text-3xl text-[color:var(--text-main)] leading-none mt-1">
                        Creative Brief
                    </h2>
                </div>
                <button
                    type="button"
                    data-testid={FORGE.demoBriefButton}
                    onClick={onLoadDemo}
                    className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] hover:text-[color:var(--profecia-gold)] transition-colors"
                >
                    · load demo brief
                </button>
            </div>

            <textarea
                data-testid={FORGE.briefTextarea}
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={5}
                placeholder="Describe your creative brief — subject, mood, brand context, hero moment…"
                className="w-full resize-none rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-hover)] p-4 text-[15px] font-light leading-relaxed text-[color:var(--text-main)] placeholder:text-[color:var(--text-muted)] placeholder:font-light focus:outline-none focus:border-[color:var(--profecia-gold)] focus:bg-white transition-colors duration-200"
            />

            <ReferenceUploader
                files={files}
                setFiles={setFiles}
                disabled={running}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                <SelectorField label="Driving LLM" testId={FORGE.llmSelect}>
                    <select
                        data-testid={FORGE.llmSelect}
                        value={llmKey}
                        onChange={(e) => setLlmKey(e.target.value)}
                        className="native-select"
                    >
                        {config.llm_options.map((o) => (
                            <option key={o.key} value={o.key}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </SelectorField>

                <SelectorField label="Brand Profile" testId={FORGE.brandSelect}>
                    <select
                        data-testid={FORGE.brandSelect}
                        value={brandIdx}
                        onChange={(e) => setBrandIdx(Number(e.target.value))}
                        className="native-select"
                    >
                        {config.brands.map((b, i) => (
                            <option key={b.name} value={i}>
                                {b.name}
                            </option>
                        ))}
                    </select>
                </SelectorField>

                <SelectorField
                    label="Content Type"
                    testId={FORGE.contentTypeSelect}
                >
                    <select
                        data-testid={FORGE.contentTypeSelect}
                        value={contentIdx}
                        onChange={(e) => setContentIdx(Number(e.target.value))}
                        className="native-select"
                    >
                        {config.content_types.map((c, i) => (
                            <option key={c} value={i}>
                                {c}
                            </option>
                        ))}
                    </select>
                </SelectorField>

                <SelectorField
                    label="Aspect Ratio"
                    testId={FORGE.aspectSelect}
                >
                    <select
                        data-testid={FORGE.aspectSelect}
                        value={aspectIdx}
                        onChange={(e) => setAspectIdx(Number(e.target.value))}
                        className="native-select"
                    >
                        {config.aspect_ratios.map((a, i) => (
                            <option key={a} value={i}>
                                {a}
                            </option>
                        ))}
                    </select>
                </SelectorField>
            </div>

            {error && (
                <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-2">
                    {error}
                </div>
            )}

            <div className="flex items-center gap-3 mt-6">
                {running ? (
                    <button
                        data-testid={FORGE.stopButton}
                        onClick={onStop}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[color:var(--text-main)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                        <Square size={14} strokeWidth={2.5} /> Stop Pipeline
                    </button>
                ) : (
                    <button
                        data-testid={FORGE.runButton}
                        onClick={onRun}
                        disabled={disabled}
                        className="inline-flex items-center gap-2.5 px-6 py-3 rounded-md bg-[color:var(--profecia-gold)] text-white text-sm font-medium tracking-wide hover:bg-[color:var(--profecia-gold-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-[1px] card-ambient"
                    >
                        <Play size={14} strokeWidth={2.5} fill="white" />
                        Run Agent Pipeline
                    </button>
                )}
                <button
                    data-testid={FORGE.resetButton}
                    onClick={() => {
                        setBrief("");
                    }}
                    disabled={running}
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-md text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] disabled:opacity-40 transition-colors"
                >
                    <RotateCcw size={14} /> Clear
                </button>
                <div className="ml-auto text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)]">
                    {config.agents.length} agents · streaming
                </div>
            </div>

            <style>{`
                .native-select {
                    width: 100%;
                    appearance: none;
                    background-color: var(--surface-hover);
                    background-image: url("data:image/svg+xml;utf8,<svg fill='%23767269' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 011.08 1.04l-4.25 4.4a.75.75 0 01-1.08 0l-4.25-4.4a.75.75 0 01.02-1.06z' clip-rule='evenodd'/></svg>");
                    background-repeat: no-repeat;
                    background-position: right 12px center;
                    background-size: 14px;
                    border: 1px solid var(--border-subtle);
                    border-radius: 6px;
                    padding: 10px 36px 10px 12px;
                    font-family: "Manrope", sans-serif;
                    font-size: 13px;
                    color: var(--text-main);
                    cursor: pointer;
                    transition: border-color 200ms ease, background-color 200ms ease;
                }
                .native-select:focus {
                    outline: none;
                    border-color: var(--profecia-gold);
                    background-color: #fff;
                }
                .native-select:hover:not(:focus) {
                    border-color: var(--profecia-gold-subtle);
                }
            `}</style>
        </div>
    );
}

function SelectorField({ label, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] mb-1.5 block">
                {label}
            </span>
            {children}
        </label>
    );
}
